"use client";

import { useState } from "react";
import { MoreHorizontal, Flag, Ban } from "lucide-react";

import { ReportContentDialog } from "@/components/report-content-dialog";
import { LoginPromptDialog } from "@/components/login-prompt-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

export type CardReportTargetType =
  | "VIDEO"
  | "FORUM_POST"
  | "COMMENT"
  | "USER"
  | "MESSAGE"
  | "STUDY_GROUP_RESOURCE";

interface CardReportMenuProps {
  targetId: string;
  targetType: CardReportTargetType;
  /** When set (and this is not the viewer's own content), overflow also offers Block. */
  authorId?: string;
  isAuthenticated?: boolean;
  loginTitle?: string;
  loginDescription?: string;
  /** Optional className for the trigger button (e.g. compact sizing). */
  className?: string;
  /** Icon size override (defaults to h-4 w-4). */
  iconClassName?: string;
}

/**
 * Reusable 3-dot menu that exposes a "Report" action on a content
 * card, plus Block when an authorId is known. Opens the standard
 * ReportContentDialog when the user is signed in, or LoginPromptDialog
 * otherwise. Use this on every non-own card surface so report/block
 * stay consistent. Canonical card action buttons (Celebrate/Comment/
 * Bookmark) must not be changed.
 */
export function CardReportMenu({
  targetId,
  targetType,
  authorId,
  isAuthenticated,
  loginTitle = "Login to report",
  loginDescription = "You need to be logged in to report content. Please log in or create an account to continue.",
  className,
  iconClassName,
}: CardReportMenuProps) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reported, setReported] = useState(false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const { toast } = useToast();
  const canBlock = Boolean(authorId);

  const requestAuth = (then: () => void) => {
    setTimeout(() => {
      if (isAuthenticated) {
        then();
      } else {
        setLoginPromptOpen(true);
      }
    }, 0);
  };

  const handleBlock = async () => {
    if (!authorId) return;
    setBlockBusy(true);
    try {
      const res = await fetch("/api/blocks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: authorId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast({
          title: "Could not block user",
          description: data.error || "Please try again.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "User blocked",
        description: "Their posts and videos are hidden, and they cannot message you.",
      });
      setBlockOpen(false);
    } catch {
      toast({
        title: "Could not block user",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBlockBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={
              reported
                ? "text-red-600 p-1 rounded-lg"
                : (className ??
                  "text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors")
            }
            aria-label={reported ? "Reported" : "Content actions"}
            onClick={(e) => e.stopPropagation()}
          >
            {reported ? (
              <Flag className={iconClassName ?? "h-4 w-4"} />
            ) : (
              <MoreHorizontal className={iconClassName ?? "h-4 w-4"} />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuItem
            onClick={(e) => e.stopPropagation()}
            onSelect={() => {
              requestAuth(() => setReportOpen(true));
            }}
            className="text-red-600 focus:text-red-700"
          >
            <Flag className="h-3.5 w-3.5 mr-2" />
            Report
          </DropdownMenuItem>
          {canBlock ? (
            <DropdownMenuItem
              onClick={(e) => e.stopPropagation()}
              onSelect={() => {
                requestAuth(() => setBlockOpen(true));
              }}
              className="text-red-600 focus:text-red-700"
            >
              <Ban className="h-3.5 w-3.5 mr-2" />
              Block
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      <ReportContentDialog
        targetId={targetId}
        targetType={targetType}
        authorId={authorId}
        open={reportOpen}
        onOpenChange={setReportOpen}
        onReportSubmitted={() => setReported(true)}
      />
      <AlertDialog open={blockOpen} onOpenChange={setBlockOpen}>
        <AlertDialogContent className="bg-white" onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Block this user?</AlertDialogTitle>
            <AlertDialogDescription>
              You will not see their posts or videos, and they cannot message or follow you.
              You can unblock them later in Privacy Settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={blockBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={blockBusy}
              onClick={(e) => {
                e.preventDefault();
                void handleBlock();
              }}
            >
              {blockBusy ? "Blocking…" : "Block"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <LoginPromptDialog
        open={loginPromptOpen}
        onOpenChange={setLoginPromptOpen}
        title={loginTitle}
        description={loginDescription}
      />
    </>
  );
}
